import { IFrozenRate } from '@entities/finance';
import { useRequestExchange } from '@entities/finance/model';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { IAccount } from '@entities/accounts/model';

let timeoutTimer: ReturnType<typeof setTimeout> | null = null;

interface Props {
  fromAccount: IAccount | null;
  toAccount: IAccount | null;
}

export const useCurrencyRate = ({ fromAccount, toAccount }: Props) => {
  const { mutate: requestExchange } = useRequestExchange();
  const [sig, setSig] = useState<string | null>(null);

  const { rate } = sig ? jwtDecode<IFrozenRate>(sig) : {};

  const debouncedConvertDetails = useMemo(
    () =>
      _.debounce((fromAccountId: string, toAccountId: string) => {
        requestExchange(
          { fromAccountId, toAccountId },
          {
            onSuccess: (data) => {
              setSig(data.sig);

              try {
                const decoded = jwtDecode<IFrozenRate>(data.sig);
                const timeout = Math.floor(decoded.exp * 1000 - Date.now());

                if (timeout && !_.isNaN(timeout)) {
                  timeoutTimer = setTimeout(
                    () => debouncedConvertDetails(fromAccountId, toAccountId),
                    timeout,
                  );
                }
              } catch (error) {
                console.error('Error decoding JWT:', error);
              }
            },
            onError: () => {},
          },
        );
      }),

    // TODO: check deps and add/remove missing if needed. Add comment if deps is as needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (fromAccount && toAccount) {
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
      }

      debouncedConvertDetails(fromAccount?.id, toAccount?.id);
    }

    return () => {
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
      }
    };
  }, [debouncedConvertDetails, fromAccount, toAccount]);

  return { rate };
};
